import type { OptimizerJobRow } from './types';

type OptimizerMaterializationProgressJob = Pick<
    OptimizerJobRow,
    | 'outcomeMode'
    | 'strategyExitOutcomeSetId'
    | 'strategyExitOutcomeSetCompletedWorkUnitCount'
    | 'strategyExitOutcomeSetMaterializerWorkUnitCount'
    | 'strategyExitOutcomeSetFailedWorkUnitCount'
    | 'strategyExitOutcomeSetAttemptedCount'
    | 'strategyExitOutcomeSetEntryCount'
    | 'strategyExitOutcomeSetStatus'
    | 'materializationPhase'
    | 'materializationCompletedCells'
    | 'materializationTotalRows'
    | 'materializationTotalColumns'
    | 'materializationCompletedColumns'
    | 'optimizerDatasetId'
    | 'status'
>;

const clamp01 = (value: number): number => {
    if (!Number.isFinite(value)) return 0;
    return Math.min(1, Math.max(0, value));
};

export function nonNegativeCount(value: number | null | undefined): number | null {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
    return Math.floor(value);
}

export function formatCount(value: number | null | undefined): string {
    const count = nonNegativeCount(value);
    return count === null ? '-' : count.toLocaleString();
}

export function formatOptimizerMaterializationPhase(phase: OptimizerJobRow['materializationPhase'] | null | undefined): string {
    switch (phase) {
        case 'feature_analysis':
            return 'Feature analysis';
        case 'column_chunks':
            return 'Column chunks';
        case 'search':
            return 'Search';
        case 'validation':
            return 'Validation';
        case 'none':
        case null:
        case undefined:
            return 'Pending';
    }
}

export function optimizerMaterializationProgress(job: OptimizerMaterializationProgressJob): {
    phaseLabel: string;
    percent: number | null;
    detail: string;
} {
    if (job.outcomeMode === 'strategy_exit' && job.strategyExitOutcomeSetId !== null && job.strategyExitOutcomeSetStatus !== 'ready') {
        const completedWorkUnits = nonNegativeCount(job.strategyExitOutcomeSetCompletedWorkUnitCount);
        const totalWorkUnits = nonNegativeCount(job.strategyExitOutcomeSetMaterializerWorkUnitCount);
        const failedWorkUnits = nonNegativeCount(job.strategyExitOutcomeSetFailedWorkUnitCount);
        const attemptedEntries = nonNegativeCount(job.strategyExitOutcomeSetAttemptedCount);
        const totalEntries = nonNegativeCount(job.strategyExitOutcomeSetEntryCount);
        const outcomeSetStatus = job.strategyExitOutcomeSetStatus;

        const phaseLabel =
            outcomeSetStatus === 'failed'
                ? 'Strategy exits failed'
                : outcomeSetStatus === 'cancelled'
                  ? 'Strategy exits cancelled'
                  : 'Strategy exits';

        const detailParts: string[] = [];

        if (completedWorkUnits !== null && totalWorkUnits !== null && totalWorkUnits > 0) {
            detailParts.push(`${completedWorkUnits.toLocaleString()} / ${totalWorkUnits.toLocaleString()} work units`);
        }

        if (attemptedEntries !== null && totalEntries !== null && totalEntries > 0) {
            detailParts.push(`${attemptedEntries.toLocaleString()} / ${totalEntries.toLocaleString()} entries`);
        }

        if (failedWorkUnits !== null && failedWorkUnits > 0) {
            detailParts.push(`${failedWorkUnits.toLocaleString()} failed`);
        }

        return {
            phaseLabel,
            percent:
                completedWorkUnits !== null && totalWorkUnits !== null && totalWorkUnits > 0
                    ? clamp01(completedWorkUnits / totalWorkUnits)
                    : null,
            detail: detailParts.length > 0 ? detailParts.join(' · ') : `outcome set ${job.strategyExitOutcomeSetId}`
        };
    }

    const phaseLabel = formatOptimizerMaterializationPhase(job.materializationPhase);
    const completedCells = nonNegativeCount(job.materializationCompletedCells);
    const totalRows = nonNegativeCount(job.materializationTotalRows);
    const totalColumns = nonNegativeCount(job.materializationTotalColumns);
    const completedColumns = nonNegativeCount(job.materializationCompletedColumns);

    const withDataset = (detail: string): string => {
        return job.optimizerDatasetId === null ? detail : `${detail} · dataset ${job.optimizerDatasetId}`;
    };

    if (completedCells !== null && totalRows !== null && totalColumns !== null && totalRows > 0 && totalColumns > 0) {
        const totalCells = totalRows * totalColumns;
        return {
            phaseLabel,
            percent: totalCells > 0 ? clamp01(completedCells / totalCells) : null,
            detail: withDataset(`${completedCells.toLocaleString()} / ${totalCells.toLocaleString()} cells`)
        };
    }

    if (completedColumns !== null && totalColumns !== null && totalColumns > 0) {
        return {
            phaseLabel,
            percent: clamp01(completedColumns / totalColumns),
            detail: withDataset(`${completedColumns.toLocaleString()} / ${totalColumns.toLocaleString()} columns`)
        };
    }

    if (totalRows !== null || totalColumns !== null) {
        return {
            phaseLabel,
            percent: null,
            detail: withDataset(`${formatCount(totalRows)} rows / ${formatCount(totalColumns)} columns`)
        };
    }

    return {
        phaseLabel,
        percent: null,
        detail: withDataset(job.status === 'completed' ? 'Ready' : '-')
    };
}
